import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/Layout';
import { LoadingState } from '@/components/EmptyState';
import { useAuth } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import { LoginPage } from '@/pages/Login';
import { OverviewPage } from '@/pages/Overview';
import { QueuePage } from '@/pages/Queue/List';
import { SellersListPage } from '@/pages/Sellers/List';
import { SellerDetailPage } from '@/pages/Sellers/Detail';
import { InventoryListPage } from '@/pages/Inventory/List';
import { InventoryDetailPage } from '@/pages/Inventory/Detail';
import { InboundsListPage } from '@/pages/Inbounds/List';
import { InboundDetailPage } from '@/pages/Inbounds/Detail';
import { InboundCreatePage } from '@/pages/Inbounds/Create';
import { PutawayQueuePage } from '@/pages/Putaway/Queue';
import { OrderDetailPage } from '@/pages/Orders/Detail';
import { OrderCreatePage } from '@/pages/Orders/Create';
import { ShipmentsListPage } from '@/pages/Shipments/List';
import { ShipmentDetailPage } from '@/pages/Shipments/Detail';
import { ReturnsListPage } from '@/pages/Returns/List';
import { ReturnDetailPage } from '@/pages/Returns/Detail';
import { BillingPage } from '@/pages/Billing/Overview';
import { ImportsListPage } from '@/pages/Imports/List';
import { ImportWizardPage } from '@/pages/Imports/Wizard';
import { WarehousesListPage } from '@/pages/Warehouses/List';
import { WarehouseDetailPage } from '@/pages/Warehouses/Detail';
import { MoveScanPage } from '@/pages/Warehouse/MoveScan';
import { UserDetailPage } from '@/pages/Users/Detail';
import { AcceptLinkPage } from '@/pages/AcceptLink';
import { UsersPage } from '@/pages/Users/List';
import { ProblemsPage } from '@/pages/Problems/List';
import { ExceptionsPage } from '@/pages/Exceptions/List';
import { SettingsPage } from '@/pages/Settings';
import { ChannelsListPage } from '@/pages/Channels/List';
import { OnboardingPage } from '@/pages/Onboarding/Overview';
import { SubscriptionPage } from '@/pages/Subscription/Overview';
import { ScannerCheckPage } from '@/pages/ScannerCheck';

/** Old `/orders` URLs keep working: the section is now "Shipments" (query and filters are carried over). */
function OrdersRedirect(): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={`/shipments${search}`} replace />;
}
function OrderCardRedirect(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/shipments/${id}${search}`} replace />;
}

function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { state } = useAuth();
  const t = useT();
  if (state.status === 'loading') return <LoadingState label={t('nav.checkingSession')} />;
  if (state.status !== 'authenticated') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Scanner diagnostics: no login, no business data - opened on the handheld itself. */}
      <Route path="/scanner-check" element={<ScannerCheckPage />} />
      <Route path="/invite" element={<AcceptLinkPage kind="invite" />} />
      <Route path="/recover" element={<AcceptLinkPage kind="recover" />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="queue" element={<QueuePage />} />
        <Route path="sellers" element={<SellersListPage />} />
        <Route path="sellers/:id" element={<SellerDetailPage />} />
        <Route path="inventory" element={<InventoryListPage />} />
        <Route path="inventory/:skuId/:ownerId" element={<InventoryDetailPage />} />
        <Route path="inbounds" element={<InboundsListPage />} />
        <Route path="inbounds/new" element={<InboundCreatePage />} />
        <Route path="inbounds/:id" element={<InboundDetailPage />} />
        <Route path="putaway" element={<PutawayQueuePage />} />
        <Route path="orders" element={<OrdersRedirect />} />
        <Route path="orders/new" element={<OrderCreatePage />} />
        <Route path="orders/:id" element={<OrderCardRedirect />} />
        <Route path="shipments" element={<ShipmentsListPage />} />
        <Route path="shipments/doc/:id" element={<ShipmentDetailPage />} />
        <Route path="shipments/:id" element={<OrderDetailPage />} />
        <Route path="returns" element={<ReturnsListPage />} />
        <Route path="returns/:id" element={<ReturnDetailPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="imports" element={<ImportsListPage />} />
        <Route path="imports/new" element={<ImportWizardPage />} />
        <Route path="imports/:id" element={<ImportWizardPage />} />
        <Route path="warehouses" element={<WarehousesListPage />} />
        <Route path="warehouses/:id" element={<WarehouseDetailPage />} />
        <Route path="warehouse/move-scan" element={<MoveScanPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="channels" element={<ChannelsListPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="problems" element={<ProblemsPage />} />
        <Route path="exceptions" element={<ExceptionsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
